import { Application, LogLevel, PlatformEnvironment, Team } from '@mews/enums';
import { sendRequest } from '../../utils';
import { SentryReporterService } from '../sentryReporter';
import { LoggerAdapter } from './types';

/*
 * New Relic supports only scalar JSON types and compound (or nested) objects.
 * https://docs.newrelic.com/docs/logs/log-management/log-api/introduction-log-api/#log-attribute-examples
 */
interface CustomAttributes {
    [index: string]: string | number | boolean | null | CustomAttributes;
}

interface Options {
    apiEndpoint: string;
    apiInsertKey: string;
    getTimestamp: () => number;
    commonAttributes: {
        team: Team;
        environment: PlatformEnvironment;
        app: {
            name: Application;
            version: string;
        };
    } & CustomAttributes;
    sentryReporterService?: SentryReporterService;
}

const enabledEnvironments = [PlatformEnvironment.Demo, PlatformEnvironment.Development, PlatformEnvironment.Production];

const createNewRelicLoggerAdapter = ({
    apiEndpoint,
    apiInsertKey,
    getTimestamp,
    commonAttributes,
    sentryReporterService,
}: Options): LoggerAdapter => {
    const doLog = (level: LogLevel, type: string, message: string, logAttributes = {}) => {
        if (!enabledEnvironments.includes(commonAttributes.environment)) {
            return;
        }
        const requestBody = {
            message,
            timestamp: getTimestamp(),
            function: 'fe',
            level,
            type,
            ...commonAttributes,
            app: {
                ...commonAttributes.app,
                platform: 'Web',
            },
            ...logAttributes,
        };
        sendRequest(
            apiEndpoint,
            {
                accept: '*/*',
                customHeaders: {
                    'X-Insert-Key': apiInsertKey,
                    'Content-Type': 'application/json',
                },
            },
            requestBody
        ).catch((error) => {
            sentryReporterService?.logWarning({ error, extra: { origin: 'newRelicLoggerAdapter' } });
        });
    };

    return {
        log: (type: string, message: string, params = {}) => doLog(LogLevel.Info, type, message, params),
        warn: (type: string, message: string, params = {}) => doLog(LogLevel.Warn, type, message, params),
        error: (type: string, message: string, params = {}) => doLog(LogLevel.Error, type, message, params),
        info: (type: string, message: string, params = {}) => doLog(LogLevel.Info, type, message, params),
    };
};

export default createNewRelicLoggerAdapter;
